import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Loader2,
  Image as ImageIcon,
  Maximize2,
  Calendar,
} from "lucide-react";

interface MediaItem {
  id: number;
  title: string;
  type: "image";
  storage_type: "file" | "link";
  url: string;
  created_at: string;
}

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/photo-gallery`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;
const backendUrl = "https://www.sevaafoundation.com/seva-backend";


const PhotoGallery = () => {
  const [photos, setPhotos] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/media/public_list.php?type=image`
      );

      const data = await response.json();

      if (data.success) {
        setPhotos(data.media);
      }
    } catch (err) {
      console.error("Failed to fetch photos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getPhotoSrc = (photo: MediaItem) => {
    if (!photo.url) {
      return defaultImage;
    }

    if (photo.storage_type === "link" || photo.url.startsWith("http://") || photo.url.startsWith("https://")) {
      return photo.url;
    }

    return `${backendUrl}${photo.url}`;
  };

  const formatDate = (date?: string) => {
    if (!date) return "SEVAA Foundation";

    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const galleryOgImage = photos.length > 0 ? getPhotoSrc(photos[0]) : defaultImage;

  const photoGallerySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${pageUrl}#collectionpage`,
    name: "Photo Gallery | SEVAA Foundation",
    alternateName: "SEVAA Foundation NGO Photo Gallery",
    url: pageUrl,
    inLanguage: ["en-IN", "hi-IN"],
    description:
      "Explore SEVAA Foundation photo gallery featuring NGO field work, child education, healthcare camps, food donation, women empowerment, tree plantation, elderly care, volunteer work and social impact photos.",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "SEVAA Foundation",
      url: siteUrl,
    },
    publisher: {
      "@type": "NGO",
      "@id": `${siteUrl}/#ngo`,
      name: "SEVAA Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: logoImage,
      },
      areaServed: [
        {
          "@type": "Country",
          name: "India",
        },
        {
          "@type": "Place",
          name: "Noida",
        },
        {
          "@type": "Place",
          name: "Jharkhand",
        },
        {
          "@type": "Place",
          name: "Chatra",
        },
        {
          "@type": "Place",
          name: "Pakur",
        },
      ],
    },
    about: [
      "NGO Photo Gallery",
      "Social Welfare Photos",
      "Child Education",
      "Healthcare Support",
      "Food Donation",
      "Women Empowerment",
      "Tree Plantation",
      "Old Age Support",
      "Volunteer Work",
      "CSR Impact",
      "Community Outreach",
      "Rural Development",
      "Tribal Community Support",
    ],
    hasPart: photos.map((photo) => ({
      "@type": "ImageObject",
      name: photo.title,
      caption: `${photo.title} - SEVAA Foundation NGO photo`,
      contentUrl: getPhotoSrc(photo),
      thumbnailUrl: getPhotoSrc(photo),
      uploadDate: photo.created_at,
      creator: {
        "@type": "NGO",
        name: "SEVAA Foundation",
        url: siteUrl,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Photo Gallery",
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Photo Gallery | SEVAA Foundation</title>

        <meta name="title" content="Photo Gallery | SEVAA Foundation" />

        <meta name="description" content="Explore SEVAA Foundation photo gallery with NGO field work, education, healthcare camps, food donation and social welfare impact photos." />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="Photo Gallery | SEVAA Foundation NGO"
        />

        <meta
          property="og:description"
          content="View SEVAA Foundation NGO photos from field work, social welfare programs, education, healthcare, food donation and community outreach."
        />

        <meta property="og:image" content={galleryOgImage} />
        <meta
          property="og:image:alt"
          content="SEVAA Foundation Photo Gallery"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="SEVAA Foundation Photo Gallery"
        />

        <meta
          name="twitter:description"
          content="Explore NGO field work and social impact photos by SEVAA Foundation."
        />

        <meta name="twitter:image" content={galleryOgImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation photo gallery" />

        <script type="application/ld+json">
          {JSON.stringify(photoGallerySchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-100 min-h-screen py-16 px-6 font-poppins">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-20">
            <span className="inline-block text-orange-600 font-black text-xs uppercase tracking-[0.25em] mb-4">
              SEVAA Foundation Memories
            </span>

            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
              Photo Gallery
            </h1>

            <p className="text-slate-500 text-lg max-w-3xl mx-auto leading-relaxed">
              Explore SEVAA Foundation&apos;s photo gallery featuring real NGO
              field work, child education, healthcare camps, food donation,
              women empowerment, tree plantation, elderly care, volunteer
              activities and social welfare impact.
            </p>
          </header>

          {isLoading ? (
            <div className="py-20 flex justify-center items-center gap-3 text-slate-500 font-semibold">
              <Loader2 className="animate-spin text-orange-500" size={30} />
              Loading photos...
            </div>
          ) : photos.length === 0 ? (
            <section className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-300">
              <ImageIcon size={52} className="mx-auto text-slate-300 mb-4" />

              <h2 className="text-xl font-bold text-slate-700 mb-2">
                No photos available yet
              </h2>

              <p className="text-slate-500">
                SEVAA Foundation photo gallery will be updated soon with NGO
                field work and impact memories.
              </p>
            </section>
          ) : (
            <section aria-labelledby="photo-list-heading">
              <h2 id="photo-list-heading" className="sr-only">
                SEVAA Foundation Photo Gallery Images
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12">
                {photos.map((photo, index) => {
                  const src = getPhotoSrc(photo);

                  return (
                    <motion.article
                      key={photo.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.04 }}
                      className="relative group h-72"
                      aria-label={`${photo.title} - SEVAA Foundation photo`}
                    >
                      {/* Stacked Layers */}
                      <div
                        className="absolute inset-0 bg-white rounded-[2rem] shadow-md translate-x-1 translate-y-1 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"
                        aria-hidden="true"
                      ></div>

                      <div
                        className="absolute inset-0 bg-white rounded-[2rem] shadow-sm translate-x-2 translate-y-2 group-hover:translate-x-6 group-hover:translate-y-6 transition-transform duration-700"
                        aria-hidden="true"
                      ></div>

                      {/* Main Content Card */}
                      <figure className="relative h-full bg-white rounded-[2rem] shadow-xl overflow-hidden border border-slate-100 group-hover:-translate-x-1 group-hover:-translate-y-1 transition-transform duration-500">
                        <img
                          src={src}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          alt={`${photo.title} - SEVAA Foundation NGO photo gallery`}
                          loading="lazy"
                        />

                        <figcaption className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <h3 className="text-white font-bold text-sm truncate">
                            {photo.title}
                          </h3>

                          <div className="flex items-center gap-1 text-white/70 text-[11px] mt-1">
                            <Calendar size={12} />
                            {formatDate(photo.created_at)}
                          </div>

                          <button
                            onClick={() => window.open(src, "_blank")}
                            className="mt-3 text-orange-400 text-xs flex items-center gap-1 font-black uppercase w-fit hover:text-orange-300"
                            aria-label={`View full photo: ${photo.title}`}
                          >
                            <Maximize2 size={12} /> View Full
                          </button>
                        </figcaption>
                      </figure>
                    </motion.article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

export default PhotoGallery;